import { useEffect } from 'react';
import type { ReactNode } from 'react';

/** The shell every "are you sure" in this app shares.
 *
 * Only the shell. What a dialog asks and what its answers are called belong
 * to the screen asking, and pushing those into props here would have produced
 * a component with an `onEverything` on it for the sake of one caller. What
 * is genuinely common is the part that is easy to get wrong and invisible
 * when you do: Escape backing out, the backdrop being clickable, the click
 * inside it not falling through to that backdrop, and the panel announcing
 * itself as a dialog labelled by its own heading.
 *
 * No action is pre-chosen and none is destructive-by-default. A dialog exists
 * because the answer is not obvious; making one of them the easy one to hit
 * by accident would defeat the point of asking.
 */
export default function ConfirmDialog({
  title,
  titleId,
  children,
  actions,
  onCancel,
}: {
  title: ReactNode;
  /** Ties the panel to its heading for a screen reader. Unique per dialog. */
  titleId: string;
  /** The body: what confirming will actually do. */
  children?: ReactNode;
  /** The answers, in the caller's own words. */
  actions: ReactNode;
  onCancel: () => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal" onClick={onCancel}>
      <div
        className="modal__panel card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId}>{title}</h2>
        {children}
        <div className="modal__actions">{actions}</div>
      </div>
    </div>
  );
}
